import { type User } from 'firebase/auth';
import BaseStore from './BaseStore';
import { ActionTypes } from '../Constants';

type LoadingState =
   | 'initializing'
   | 'restoring_session'
   | 'connecting_database'
   | 'syncing'
   | 'ready'
   | 'idle';

/**
 * Explicit workspace readiness state machine.
 * Ensures Firebase login does NOT imply "workspace ready".
 */
export type WorkspaceStatus =
   | 'SIGNED_OUT'
   | 'AUTHENTICATED'
   | 'PROVISIONING'
   | 'READY';

export interface WorkspaceState {
   status: WorkspaceStatus;
   user: User | null;
   degradedReason?: string;
}

/**
 * Tracks sync health for UI visibility.
 */
export interface SyncHealth {
   lastSuccessAt?: number;
   lastErrorAt?: number;
   lastErrorCode?: string;
   pendingChanges?: number;
   isActive: boolean;
}

class AuthStore extends BaseStore {
   private user: User | null = null;
   private isAuthenticated = false;
   private isLoading = true;
   private loadingState: LoadingState = 'initializing';
   private error: string | null = null;
   private couchStatus: 'unknown' | 'ready' | 'pending' | 'error' = 'unknown';
   private couchMessage: string | null = null;
   private syncBlockedReason: string | null = null;
   private syncBlockedMessage: string | null = null;
   private credentialsNeedRefresh = false;

   // Explicit workspace state machine
   private workspaceStatus: WorkspaceStatus = 'SIGNED_OUT';
   private degradedReason: string | null = null;

   // Sync health tracking
   private syncHealth: SyncHealth = {
      isActive: false,
   };

   constructor() {
      super();

      this.register((action) => {
         switch (action.type) {
            case ActionTypes.LOGIN_SUCCESS:
            case ActionTypes.SIGNUP_SUCCESS:
               this.user = (action.user as User) || null;
               this.isAuthenticated = true;
               this.isLoading = false;
               this.loadingState = 'ready';
               this.error = null;
               this.couchStatus =
                  (action.couchStatus as typeof this.couchStatus) || this.couchStatus;
               this.couchMessage = (action.couchMessage as string) || null;
               // Set workspace to AUTHENTICATED (not READY yet - Couch may still need provisioning)
               this.workspaceStatus = 'AUTHENTICATED';
               this.degradedReason = null;
               this.emitChange();
               break;

            case ActionTypes.LOGIN_ERROR:
            case ActionTypes.SIGNUP_ERROR:
               this.user = null;
               this.isAuthenticated = false;
               this.isLoading = false;
               this.loadingState = 'idle';
               this.error = (action.error as string) || null;
               this.couchStatus = 'error';
               this.couchMessage = (action.couchMessage as string) || null;
               this.emitChange();
               break;

            case ActionTypes.LOGOUT:
               this.user = null;
               this.isAuthenticated = false;
               this.isLoading = false;
               this.loadingState = 'idle';
               this.error = null;
               this.couchStatus = 'unknown';
               this.couchMessage = null;
               // Reset workspace state machine
               this.workspaceStatus = 'SIGNED_OUT';
               this.degradedReason = null;
               this.syncHealth = { isActive: false };
               this.emitChange();
               break;

            case ActionTypes.AUTH_STATE_CHANGED:
               this.user = (action.user as User) || null;
               this.isAuthenticated = !!action.user;
               this.isLoading = false;
               this.loadingState = action.user ? 'restoring_session' : 'idle';
               if (!action.user) {
                  this.couchStatus = 'unknown';
                  this.couchMessage = null;
               }
               this.emitChange();
               break;

            case ActionTypes.COUCH_STATUS_CHANGED:
               this.couchStatus =
                  (action.couchStatus as typeof this.couchStatus) || this.couchStatus;
               this.couchMessage = (action.couchMessage as string) || null;

               // Update loading state based on couch status
               if (this.couchStatus === 'ready') {
                  this.loadingState = 'ready';
               } else if (this.couchStatus === 'pending') {
                  this.loadingState = 'connecting_database';
               }

               this.emitChange();
               break;

            case ActionTypes.SYNC_BLOCKED:
               this.syncBlockedReason = (action.reason as string) || null;
               this.syncBlockedMessage = (action.message as string) || null;
               this.couchStatus = 'error';
               this.emitChange();
               break;

            case ActionTypes.CREDENTIALS_REFRESH_NEEDED:
               this.credentialsNeedRefresh = true;
               this.couchStatus = 'pending';
               this.couchMessage = 'Refreshing database credentials...';
               this.workspaceStatus = 'PROVISIONING';
               this.emitChange();
               break;

            // Workspace state machine transitions
            case ActionTypes.WORKSPACE_AUTHENTICATED:
               this.workspaceStatus = 'AUTHENTICATED';
               this.degradedReason = null;
               this.emitChange();
               break;

            case ActionTypes.WORKSPACE_PROVISIONING:
               this.workspaceStatus = 'PROVISIONING';
               this.degradedReason = null;
               this.emitChange();
               break;

            case ActionTypes.WORKSPACE_READY:
               this.workspaceStatus = 'READY';
               this.degradedReason = null;
               this.couchStatus = 'ready';
               this.loadingState = 'ready';
               this.emitChange();
               break;

            // Sync health updates
            case ActionTypes.SYNC_HEALTH_UPDATE:
               this.syncHealth = {
                  ...this.syncHealth,
                  ...(action.health as Partial<SyncHealth>),
               };
               this.emitChange();
               break;

            default:
            // Do nothing
         }
      });
   }

   getUser(): User | null {
      return this.user;
   }

   getIsAuthenticated(): boolean {
      return this.isAuthenticated;
   }

   getIsLoading(): boolean {
      return this.isLoading;
   }

   getLoadingState(): LoadingState {
      return this.loadingState;
   }

   getLoadingStateMessage(): string {
      switch (this.loadingState) {
         case 'initializing':
            return 'Starting up...';
         case 'restoring_session':
            return 'Restoring session...';
         case 'connecting_database':
            return 'Connecting to database...';
         case 'syncing':
            return 'Syncing...';
         case 'ready':
            return 'Ready';
         case 'idle':
            return '';
         default:
            return '';
      }
   }

   getError(): string | null {
      return this.error;
   }

   getCouchStatus(): 'unknown' | 'ready' | 'pending' | 'error' {
      return this.couchStatus;
   }

   getCouchMessage(): string | null {
      return this.couchMessage;
   }

   getSyncBlockedReason(): string | null {
      return this.syncBlockedReason;
   }

   getSyncBlockedMessage(): string | null {
      return this.syncBlockedMessage;
   }

   isSyncBlocked(): boolean {
      return this.syncBlockedReason !== null;
   }

   needsCredentialsRefresh(): boolean {
      return this.credentialsNeedRefresh;
   }

   clearCredentialsRefreshFlag(): void {
      this.credentialsNeedRefresh = false;
   }

   // Workspace state machine getters
   getWorkspaceStatus(): WorkspaceStatus {
      return this.workspaceStatus;
   }

   getWorkspaceState(): WorkspaceState {
      return {
         status: this.workspaceStatus,
         user: this.user,
         degradedReason: this.degradedReason || undefined,
      };
   }

   getDegradedReason(): string | null {
      return this.degradedReason;
   }

   isWorkspaceReady(): boolean {
      return this.workspaceStatus === 'READY';
   }

   canWriteLocally(): boolean {
      return this.workspaceStatus === 'READY';
   }

   canSyncRemotely(): boolean {
      // Remote sync only allowed in READY state
      return this.workspaceStatus === 'READY';
   }

   // Sync health getters
   getSyncHealth(): SyncHealth {
      return { ...this.syncHealth };
   }

   isSyncActive(): boolean {
      return this.syncHealth.isActive;
   }
}

const authStore = new AuthStore();

export default authStore;
